import {
  ClipboardDocumentIcon,
  EllipsisVerticalIcon
} from '@heroicons/react/20/solid'
import { useSuspenseQuery } from '@tanstack/react-query'
import type { PersonalKeycard } from 'cleverev-types'
import { useTranslation } from 'react-i18next'
import { Outlet, useParams } from 'react-router'
import { toast } from 'sonner'
import { personalKeycardAPI } from '#/api/personalKeycard'
import { Button } from '#/components/ui/button'
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm
} from '#/components/ui/description-list'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '#/components/ui/dropdown-menu'
import { Separator } from '#/components/ui/separator'
import { Heading } from '#/components/ui/typography'
import { copy } from '#/lib/copy'
import { formatDateTime2Digit } from '#/lib/datetime'


export default function PersonalKeycardPage() {
  const { t, i18n } = useTranslation([ 'personal_keycards', 'general' ])
  const params = useParams() as { organization: string; personalKeycard: string }

  const { data } = useSuspenseQuery({
    queryKey: [ 'organizations', params.organization, 'personalKeycards', params.personalKeycard ],
    queryFn: () => personalKeycardAPI.get(params.personalKeycard)
  })

  // Copy personal keycard id and notify
  function copyPersonalKeycardId(personalKeycard: PersonalKeycard) {
    copy(personalKeycard.id)
    toast.info(t('view_personal_keycard.id_copied'))
  }

  return (
    <div className="flex grow flex-col gap-4 items-start">

      <Outlet />

      {data.personalKeycard && (
        <>
          <div className="flex w-full items-end justify-between gap-4">
            <Heading>{`${t('view_personal_keycard.personal_keycard')} ${data.personalKeycard.id}`}</Heading>
            <div className="flex flex-row gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger>
                  <Button variant="ghost" size="icon">
                    <EllipsisVerticalIcon className="text-tertiary" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => copyPersonalKeycardId(data.personalKeycard)}>
                    <ClipboardDocumentIcon className="text-tertiary" />
                    {t('view_personal_keycard.menu.copy_id')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <Separator />

          <DescriptionList className="w-full">
            <DescriptionTerm>
              {t('fields.id')}
            </DescriptionTerm>
            <DescriptionDetails>
              {data.personalKeycard.id}
            </DescriptionDetails>
            <DescriptionTerm>
              {t('fields.hardwareId')}
            </DescriptionTerm>
            <DescriptionDetails>
              {data.personalKeycard.hardwareId ?? '-'}
            </DescriptionDetails>
            <DescriptionTerm>
              {t('fields.friendlyId')}
            </DescriptionTerm>
            <DescriptionDetails>
              {data.personalKeycard.hardwareId ?? '-'}
            </DescriptionDetails>
            <DescriptionTerm>
              {t('fields.userId')}
            </DescriptionTerm>
            <DescriptionDetails>
              {data.personalKeycard.userId ?? '-'}
            </DescriptionDetails>
            <DescriptionTerm>
              {t('fields.email')}
            </DescriptionTerm>
            <DescriptionDetails>
              {data.userEmail ?? '-'}
            </DescriptionDetails>
            <DescriptionTerm>
              {t('fields.applicationId')}
            </DescriptionTerm>
            <DescriptionDetails>
              {data.personalKeycard.applicationId ?? '-'}
            </DescriptionDetails>
            <DescriptionTerm>
              {t('fields.created_at')}
            </DescriptionTerm>
            <DescriptionDetails>
              {formatDateTime2Digit(data.personalKeycard.createdAt, i18n.language)}
            </DescriptionDetails>
            <DescriptionTerm>
              {t('fields.updated_at')}
            </DescriptionTerm>
            <DescriptionDetails>
              {formatDateTime2Digit(data.personalKeycard.updatedAt, i18n.language)}
            </DescriptionDetails>
            <DescriptionTerm>
              {t('fields.organizationId')}
            </DescriptionTerm>
            <DescriptionDetails>
              {data.personalKeycard.organizationId ?? '-'}
            </DescriptionDetails>
          </DescriptionList>
        </>
      )}

    </div>
  )
}
